import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Plus, ArrowDown } from "lucide-react";
import { IMAGES, NPN, money, PRICE_PER_BOTTLE } from "@/components/store/data";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.4 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

const HEADLINE = ["The Cellular", "Spark of", "Vitality"];

export default function Hero({ onAdd, onBuy }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBottle = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen overflow-hidden pt-16">
      <motion.img
        src={IMAGES.texture}
        style={{ y: yBg }}
        alt=""
        className="absolute inset-0 w-full h-full object-cover opacity-[0.12] pointer-events-none"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#052221]/40 to-[#052221] pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-8 items-center min-h-[calc(100vh-4rem)]">
        {/* Left copy */}
        <div className="lg:col-span-7 pt-10 lg:pt-0">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="flex items-center gap-3 mb-8 text-xs uppercase tracking-[0.25em] text-[#D4AF37]"
          >
            <span className="w-8 h-px bg-[#D4AF37]" />
            NPN {NPN} · Health Canada Licensed
          </motion.div>

          <h1 className="font-display font-bold text-[11vw] sm:text-6xl lg:text-[4.25rem] leading-[0.95] tracking-tight text-[#F4F1EB]">
            {HEADLINE.map((l, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span variants={line} custom={i} initial="hidden" animate="show" className="block">
                  {l}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-8 max-w-md text-[#B8C5C4] text-base leading-relaxed"
          >
            Health Thru Nutrition's Ubiquinol is formulated with Kaneka®
            Ubiquinol, a high-quality, stabilized CoQ10 ingredient. This Non-GMO,
            gluten-free, and soy-free supplement is manufactured in the USA under
            strict cGMP standards to ensure quality and consistency.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            className="mt-4 max-w-md text-[#B8C5C4]/70 text-xs italic leading-relaxed"
          >
            "These statements have not been evaluated by the Food and Drug
            Administration. This product is not intended to diagnose, treat, cure,
            or prevent any disease."
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-add-btn"
              onClick={onAdd}
              className="group flex items-center gap-3 bg-[#D4AF37] text-[#052221] px-7 py-4 text-sm uppercase tracking-[0.18em] font-semibold hover:bg-[#F0C851] transition-colors"
            >
              <Plus className="w-4 h-4" strokeWidth={2.5} />
              Add a bottle · {money(PRICE_PER_BOTTLE)}
            </button>
            <button
              data-testid="hero-buy-btn"
              onClick={onBuy}
              className="text-sm uppercase tracking-[0.18em] text-[#F4F1EB] border-b border-[#D4AF37]/50 pb-1 hover:border-[#D4AF37] transition-colors"
            >
              Go to checkout
            </button>
          </motion.div>
        </div>

        {/* Right bottle */}
        <div className="lg:col-span-5 relative flex justify-center">
          <div className="absolute w-[380px] h-[380px] rounded-full bg-[#D4AF37]/10 blur-3xl" />
          <motion.img
            src={IMAGES.heroBottle}
            style={{ y: yBottle, opacity }}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            alt="Ubiquinol CoQ10 supplement bottle"
            className="relative z-10 max-h-[70vh] object-contain drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)]"
          />
        </div>
      </div>

      <motion.div
        style={{ opacity }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[#B8C5C4]"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <ArrowDown className="w-4 h-4 animate-bounce" strokeWidth={1.5} />
      </motion.div>
    </section>
  );
}
